/**
 * 通知弹出面板组件
 * 替换 Header 硬编码铃铛，接入真实消息 API
 * 功能：实时未读数 + 消息/公告预览 + 快捷操作
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Popover,
  Badge,
  Tabs,
  List,
  Tag,
  Button,
  Space,
  Empty,
  Typography,
  message,
} from 'antd';
import {
  BellOutlined,
  CheckOutlined,
  RightOutlined,
  PushpinFilled,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/zh-cn';
import { messageCenterApi } from '@/modules/foundation/api/messageCenter';
import type {
  MessageInfo,
  AnnouncementInfo,
} from '@/modules/foundation/api/types';

dayjs.extend(relativeTime);
dayjs.locale('zh-cn');

const { Text } = Typography;

// 消息类型标签映射
const MESSAGE_TYPE_MAP: Record<string, { label: string; color: string }> = {
  system: { label: '系统', color: 'blue' },
  notice: { label: '通知', color: 'cyan' },
  todo: { label: '待办', color: 'green' },
  alert: { label: '告警', color: 'red' },
};

// 公告级别标签映射
const ANNOUNCEMENT_LEVEL_MAP: Record<string, { label: string; color: string }> = {
  normal: { label: '普通', color: 'blue' },
  important: { label: '重要', color: 'red' },
  urgent: { label: '紧急', color: 'orange' },
};

const POLL_INTERVAL = 60000; // 60秒轮询

const NotificationPopover: React.FC = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('messages');
  const [unread, setUnread] = useState({ count: 0, messageCount: 0, announcementCount: 0 });
  const [messages, setMessages] = useState<MessageInfo[]>([]);
  const [announcements, setAnnouncements] = useState<AnnouncementInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // 获取未读数
  const fetchUnreadCount = useCallback(async () => {
    try {
      setUnread(await messageCenterApi.getUnreadCount());
    } catch {
      // 静默处理
    }
  }, []);

  // 获取最近消息
  const fetchMessages = useCallback(async () => {
    try {
      setLoading(true);
      const data = await messageCenterApi.getMessageList({ page: 1, pageSize: 5 });
      setMessages(data.list || []);
    } catch {
      // 静默处理
    } finally {
      setLoading(false);
    }
  }, []);

  // 获取最近公告
  const fetchAnnouncements = useCallback(async () => {
    try {
      const data = await messageCenterApi.getAnnouncementList({ page: 1, pageSize: 3 });
      setAnnouncements(data.list || []);
    } catch {
      // 静默处理
    }
  }, []);

  // 初始加载未读数
  useEffect(() => {
    fetchUnreadCount();
  }, [fetchUnreadCount]);

  // 轮询未读数
  useEffect(() => {
    pollTimerRef.current = setInterval(fetchUnreadCount, POLL_INTERVAL);
    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
      }
    };
  }, [fetchUnreadCount]);

  // Popover 打开时加载数据
  useEffect(() => {
    if (open) {
      fetchMessages();
      fetchAnnouncements();
    }
  }, [open, fetchMessages, fetchAnnouncements]);

  // 全部已读：只处理当前标签（消息 / 公告各管各的）
  const onAnnouncementsTab = activeTab === 'announcements';
  const handleMarkAllRead = async () => {
    try {
      if (onAnnouncementsTab) {
        await messageCenterApi.markAllAnnouncementsRead();
        setAnnouncements((prev) => prev.map((a) => ({ ...a, isRead: true })));
      } else {
        await messageCenterApi.markMessagesRead({ ids: [] });
        setMessages((prev) => prev.map((m) => ({ ...m, isRead: 1 })));
      }
      message.success('已全部标记为已读');
      void fetchUnreadCount();
    } catch {
      message.error('操作失败');
    }
  };

  // 单条标记已读
  const handleMarkRead = async (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await messageCenterApi.markMessagesRead({ ids: [id] });
      setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, isRead: 1 } : m)));
      void fetchUnreadCount();
    } catch {
      message.error('操作失败');
    }
  };

  // 点击消息跳转
  const handleMessageClick = (msg: MessageInfo) => {
    setOpen(false);
    navigate('/tenant/message-center', { state: { messageId: msg.id } });
  };

  const handleAnnouncementClick = (announcement: AnnouncementInfo) => {
    setOpen(false);
    navigate('/tenant/message-center', { state: { announcementId: announcement.id } });
  };

  // 查看全部
  const handleViewAll = () => {
    setOpen(false);
    navigate('/tenant/message-center');
  };

  // 消息列表
  const renderMessages = () => (
    <List
      loading={loading}
      dataSource={messages}
      locale={{ emptyText: <Empty description="暂无消息" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
      renderItem={(item) => (
        <List.Item
          onClick={() => handleMessageClick(item)}
          style={{ cursor: 'pointer', padding: '10px 0' }}
          extra={
            item.isRead === 0 && (
              <Button
                type="link"
                size="small"
                icon={<CheckOutlined />}
                onClick={(e) => handleMarkRead(item.id, e)}
                style={{ fontSize: 12 }}
              >
                已读
              </Button>
            )
          }
        >
          <List.Item.Meta
            avatar={
              item.isRead === 0 ? (
                <Badge status="processing" />
              ) : (
                <Badge status="default" />
              )
            }
            title={
              <Space size={4}>
                <Tag
                  color={MESSAGE_TYPE_MAP[item.type]?.color || 'default'}
                  style={{ fontSize: 11, lineHeight: '18px', padding: '0 4px' }}
                >
                  {MESSAGE_TYPE_MAP[item.type]?.label || item.type}
                </Tag>
                <Text
                  strong={item.isRead === 0}
                  style={{ fontSize: 13 }}
                  ellipsis
                >
                  {item.title}
                </Text>
              </Space>
            }
            description={
              <Text type="secondary" style={{ fontSize: 12 }}>
                {dayjs(item.createdAt).fromNow()}
              </Text>
            }
          />
        </List.Item>
      )}
    />
  );

  // 公告列表
  const renderAnnouncements = () => (
    <List
      dataSource={announcements}
      locale={{ emptyText: <Empty description="暂无公告" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
      renderItem={(item) => (
        <List.Item
          onClick={() => handleAnnouncementClick(item)}
          style={{ cursor: 'pointer', padding: '10px 0' }}
        >
          <List.Item.Meta
            avatar={!item.isRead ? <Badge status="processing" /> : item.topFlag === 1 ? <PushpinFilled style={{ color: '#fa8c16' }} /> : undefined}
            title={
              <Space size={4}>
                <Tag
                  color={ANNOUNCEMENT_LEVEL_MAP[item.level]?.color || 'blue'}
                  style={{ fontSize: 11, lineHeight: '18px', padding: '0 4px' }}
                >
                  {ANNOUNCEMENT_LEVEL_MAP[item.level]?.label || '普通'}
                </Tag>
                <Text style={{ fontSize: 13 }} ellipsis>
                  {item.title}
                </Text>
              </Space>
            }
            description={
              <Text type="secondary" style={{ fontSize: 12 }}>
                {item.publishAt ? dayjs(item.publishAt).format('MM-DD HH:mm') : dayjs(item.createdAt).format('MM-DD HH:mm')}
              </Text>
            }
          />
        </List.Item>
      )}
    />
  );

  const content = (
    <div style={{ width: 360 }}>
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        size="small"
        items={[
          {
            key: 'messages',
            label: (
              <Space size={4}>
                消息
                {unread.messageCount > 0 && <Badge count={unread.messageCount} size="small" />}
              </Space>
            ),
            children: renderMessages(),
          },
          {
            key: 'announcements',
            label: (
              <Space size={4}>
                公告
                {unread.announcementCount > 0 && <Badge count={unread.announcementCount} size="small" />}
              </Space>
            ),
            children: renderAnnouncements(),
          },
        ]}
      />
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderTop: '1px solid #f0f0f0',
          paddingTop: 8,
          marginTop: 4,
        }}
      >
        <Button
          type="link"
          size="small"
          icon={<CheckOutlined />}
          onClick={handleMarkAllRead}
          disabled={(onAnnouncementsTab ? unread.announcementCount : unread.messageCount) === 0}
        >
          全部已读
        </Button>
        <Button
          type="link"
          size="small"
          onClick={handleViewAll}
        >
          查看全部消息 <RightOutlined />
        </Button>
      </div>
    </div>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      placement="bottomRight"
      arrow={false}
      styles={{ body: { padding: '12px 16px' } }}
    >
      <Badge count={unread.count} overflowCount={99} size="small" offset={[-2, 2]}>
        <BellOutlined
          className="header-icon"
          style={{ cursor: 'pointer' }}
        />
      </Badge>
    </Popover>
  );
};

export default NotificationPopover;
